import { useCallback } from "react";

import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Icon } from "semantic-ui-react";

import type { Assign, OpenAPI } from "simplydo/interfaces";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import BaseChip, { type ChipConfiguration } from "./BaseChip";
import { SocialLinks } from "./SocialLinks";

type IUserChipBase = {
  user: Assign<OpenAPI.Schemas["User"], OpenAPI.Schemas["AssignUserPrepare"]>;
  showSocials?: boolean;
};

type IUserChip = IUserChipBase & ChipConfiguration;

function UserChip({
  user,
  actionIcon,
  actionClick,
  actionTooltip,
  showSocials,
  ...props
}: IUserChip) {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.user);

  const params = useParams();
  const matchId = params?.id;

  const hasProfile = user?.profile;
  if (!hasProfile) {
    user = { profile: { fullName: "Unknown User" } };
  }

  const { jobTitle = "", department = "" } = user.profile;
  let description = jobTitle;
  if (department) {
    if (jobTitle) {
      description += " - ";
    }
    description += department;
  }

  const userCanListUsers =
    util.hasPermission(
      currentUser,
      "org.listUsers",
      currentUser?.ownerOrganisation?._id,
    ) || util.hasPermission(currentUser, "group.listUsers", matchId);

  const userCanViewEmails = util.hasPermission(
    currentUser,
    "org.viewUserEmails",
    currentUser?.ownerOrganisation?._id,
  );

  const messageUser = useCallback(
    (member) => {
      api.messages.findThreadByUser(
        member._id,
        (data) => {
          dispatch(
            actions.messages.selectThread(
              data?.thread || {
                _id: `newThread:${member._id}`,
                participantUsers: [member],
              },
            ),
          );
        },
        (err) => toast.error(err.message),
      );
    },
    [dispatch],
  );

  const canMessage =
    user._id !== currentUser?._id &&
    currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "directMessaging",
    );
  const teamsHandle = user?.teamsHandle;
  const canMessageViaTeams =
    user._id !== currentUser?._id &&
    !!currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "teamsHandleAdvertising",
    ) &&
    !!teamsHandle;

  const messageUserViaTeams = useCallback(() => {
    if (!teamsHandle) return;
    const teamsUrl = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(teamsHandle)}`;
    window.open(teamsUrl, "_blank", "noopener,noreferrer");
  }, [teamsHandle]);

  const ghost = useCallback(() => {
    util
      .confirm(
        "Really ghost as this user?",
        "Ghosting allows you to view the platform and carry out actions as though you are the user. You can un-ghost at any time by tapping the avatar at the top-right of the screen.",
      )
      .then(() =>
        api.users.ghost(
          user?._id,
          () => {
            api.queryClient.invalidateQueries();
            dispatch(actions.user.ghost(user?._id));
            navigate("/");
          },
          (err) => toast.error(err.message),
        ),
      )
      .catch(() => {});
  }, [navigate, user?._id, dispatch]);

  const canGhost =
    user?._id &&
    !(user?._id === currentUser?._id) &&
    util.hasPermission(
      currentUser,
      "super.ghostUser",
      currentUser?.ownerOrganisation,
    );

  const hasFooterButtons =
    canMessage || canMessageViaTeams || userCanListUsers || canGhost;

  return (
    <BaseChip
      {...props}
      title={
        <>
          {user.profile.fullName}
          {showSocials ?
            <SocialLinks user={user} />
          : null}
        </>
      }
      largeTitle={
        <>
          {user.profile.fullName}
          <SocialLinks user={user} />
        </>
      }
      subtitle={description}
      content={
        <>
          {userCanViewEmails && user?.emails ?
            <p
              style={{
                marginTop: 0,
                marginBottom: 5,
                fontSize: 13,
                color: "grey",
              }}
            >
              <Icon name="mail outline" style={{ marginBottom: -10 }} />
              {user.emails[0].address}
            </p>
          : null}
          <p
            style={{
              color: "grey",
              fontSize: 13,
              marginBottom: 8,
              marginTop: 0,
            }}
          >
            {user?.profile?.tagline}
          </p>
          {(
            currentUser?.ownerOrganisation?.auth?.saml?.customMappings &&
            user?.profile?.attributes &&
            Object.keys(user.profile.attributes).length > 0
          ) ?
            <div style={{ marginBottom: 10 }}>
              {currentUser.ownerOrganisation.auth.saml.customMappings.map(
                (mapping) =>
                  user?.profile?.attributes[mapping.localKey] && (
                    <div key={mapping.localKey} style={{ marginBottom: 5 }}>
                      {mapping.displayName}:{" "}
                      {user?.profile?.attributes[mapping.localKey]}
                    </div>
                  ),
              )}
            </div>
          : null}
        </>
      }
      imageUrl={util.avatarUrl(user, "200x200")}
      labels={user.iconRoles}
      actionIcon={actionIcon}
      actionClick={actionClick}
      actionTooltip={actionClick ? actionTooltip : undefined}
      footer={
        hasFooterButtons ?
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              justifyContent: "flex-end",
              paddingLeft: "25%",
            }}
          >
            {canMessage ?
              <Button
                compact
                size="tiny"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  messageUser(user);
                }}
              >
                <Icon name="mail outline" />
                Message
              </Button>
            : null}
            {canMessageViaTeams ?
              <Button
                compact
                size="tiny"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  messageUserViaTeams();
                }}
              >
                <Icon name="microsoft" />
                Message via Teams
              </Button>
            : null}
            {userCanListUsers ?
              <Button
                compact
                size="tiny"
                onClick={() => navigate(`/users/${user?._id}`)}
              >
                <Icon name="user outline" />
                View profile
              </Button>
            : null}
            {canGhost ?
              <Button
                compact
                size="tiny"
                onClick={() => {
                  document.dispatchEvent(
                    new MouseEvent("click", { bubbles: true }),
                  );
                  ghost();
                }}
              >
                <Icon name="snapchat ghost" />
                Ghost
              </Button>
            : null}
          </div>
        : null
      }
    />
  );
}

export default UserChip;
